/* eslint-disable react/no-unescaped-entities */
/**
 * PrivacyPolicyPage · /privacy
 * ---------------------------------------------------------------------------
 * Production-grade privacy policy for ErrorHacker. Kept legally sensible but
 * scannable — every section has an "in plain English" TL;DR block above the
 * dense text.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Cookie, Database, Mail } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const Section = ({ title, tldr, children, icon: Icon }) => (
  <section className="mb-10" data-testid={`privacy-${title.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="flex items-center gap-3 mb-3">
      {Icon && <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: 'rgba(0,255,157,.14)', color: 'var(--eh-green)', border: '1px solid rgba(0,255,157,.4)' }}><Icon size={16} /></div>}
      <h2 className="eh-display font-black" style={{ fontSize: 'clamp(1.3rem, 3vw, 1.8rem)' }}>{title}</h2>
    </div>
    {tldr && (
      <div className="mb-3 p-3 rounded-lg border" style={{ borderColor: 'rgba(0,255,157,.3)', background: 'rgba(0,255,157,.06)' }}>
        <div className="eh-mono text-[10px] tracking-widest text-[var(--eh-green)] mb-1">// TL;DR</div>
        <div className="text-[13px] opacity-90 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>{tldr}</div>
      </div>
    )}
    <div className="space-y-3 text-[13.5px] opacity-85 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
      {children}
    </div>
  </section>
);

const PrivacyPolicyPage = () => {
  const { config } = useSiteConfig();
  const site = config.site || {};
  const name = site.name || 'ErrorHacker';
  const email = site.email || 'privacy@errorhacker.site';
  return (
    <div className="min-h-screen">
      <section className="relative py-16 sm:py-20 eh-grid-bg overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 50% 40%, rgba(0,255,157,0.08), transparent 60%)' }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="eh-kicker justify-center mb-4">// LEGAL · PRIVACY</div>
          <h1 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}>PRIVACY <span className="eh-neon">POLICY</span></h1>
          <div className="mt-3 eh-mono text-[11px] opacity-70">Last updated: February 2026</div>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Section title="Who we are" icon={Lock} tldr={`${name} operates https://errorhacker.site. This policy explains what data we collect, why, and how to opt out.`}>
            <p>{name} ("we", "us", "our") is the operator of https://errorhacker.site and the associated Telegram bot channels. If you're reading this, you're either a customer, a visitor, or an operator we've worked with — this policy applies to all of you.</p>
            <p>Questions about privacy? Email <a href={`mailto:${email}`} className="eh-neon underline decoration-dotted">{email}</a> or DM us on Telegram.</p>
          </Section>

          <Section title="What we collect" icon={Database} tldr="Only what we need to run the service: your email, order details, and — if you agree to it in the cookie banner — anonymised usage analytics.">
            <p><strong className="text-white">Account data</strong> — email, name, hashed password, wallet balance, order history. Never sold.</p>
            <p><strong className="text-white">Order data</strong> — items purchased, payment method, amount, and (for recovery ops) the platform + issue you flagged. Required to fulfil the order.</p>
            <p><strong className="text-white">Chat data</strong> — messages you send through the Telegram bot or support inbox. Retained for 90 days after ticket resolution, then wiped.</p>
            <p><strong className="text-white">Usage data</strong> — pages visited, buttons clicked, IP and user-agent (for fraud prevention). Only collected if you accept analytics cookies.</p>
            <p><strong className="text-white">Payment data</strong> — never touches our servers. Cashfree (India) and manual UPI/crypto flows are handled by the respective providers.</p>
          </Section>

          <Section title="Cookies" icon={Cookie} tldr="Three categories: essential (always on), analytics (opt-in), marketing (opt-in). Change your mind anytime by clearing site data.">
            <p><strong className="text-white">Essential</strong> — session token, cart, CSRF. The site literally can't work without these.</p>
            <p><strong className="text-white">Analytics</strong> — GA4 measurement. Anonymised IPs. Aggregated only. We use this to figure out which pages help you and which don't.</p>
            <p><strong className="text-white">Marketing</strong> — retargeting pixels for our own campaigns. No third-party ad networks resell your data.</p>
          </Section>

          <Section title="How we use it" tldr="Deliver your order, keep your account secure, and (with consent) show you improvements you'd care about.">
            <ul className="list-disc pl-5 space-y-1">
              <li>Fulfil your orders and provide customer support</li>
              <li>Send transactional emails (receipts, order updates, refund confirmations)</li>
              <li>Detect fraud, abuse, and multi-account violations</li>
              <li>Improve the product — anonymised analytics only, if you opted in</li>
              <li>Comply with legal requests (subpoenas, DMCA, take-down orders)</li>
            </ul>
            <p><strong className="text-white">We do NOT:</strong> sell your data, share your email with marketers, or feed your chat logs into third-party AI training pipelines.</p>
          </Section>

          <Section title="Third parties" tldr="We use vetted providers for infra. Each one only sees the slice of data they need to do their job.">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Cashfree</strong> — payment processing (India)</li>
              <li><strong className="text-white">Resend</strong> — transactional email</li>
              <li><strong className="text-white">Telegram</strong> — chat & bot notifications (only if you opted in)</li>
              <li><strong className="text-white">MongoDB Atlas</strong> — encrypted database (AES-256 at rest)</li>
              <li><strong className="text-white">Google Analytics 4</strong> — optional, anonymised only</li>
              <li><strong className="text-white">Anthropic / Google / OpenAI</strong> — AI security tools; queries are anonymised before dispatch</li>
            </ul>
          </Section>

          <Section title="Your rights" tldr="Access, download, delete — all by emailing us. We reply within 5 working days.">
            <p>You have the right to <strong className="text-white">access</strong>, <strong className="text-white">correct</strong>, <strong className="text-white">export</strong>, or <strong className="text-white">delete</strong> your data at any time. Deletion is irreversible and includes the wallet ledger — download it first if you need a copy.</p>
            <p>To exercise any right, email <a href={`mailto:${email}`} className="eh-neon underline decoration-dotted">{email}</a> with the subject line <em>"Privacy Request"</em>.</p>
          </Section>

          <Section title="Security" tldr="Encrypted at rest, encrypted in transit, JWT auth, no plaintext passwords. If we ever get breached, we tell you within 72 hours.">
            <p>Passwords are hashed with bcrypt. Sessions use short-lived JWTs. Database is encrypted at rest (AES-256) and behind IP allowlists. Backups are geo-redundant and encrypted with separate keys.</p>
            <p>If we ever detect a data breach, we notify affected users within 72 hours per GDPR Article 33.</p>
          </Section>

          <Section title="Contact" icon={Mail} tldr={`Privacy questions → ${email}. Legal → the same address, subject "Legal".`}>
            <p>All privacy-related requests: <a href={`mailto:${email}`} className="eh-neon underline decoration-dotted">{email}</a></p>
            <p>General contact: <a href={`mailto:${site.email || 'contact@errorhacker.site'}`} className="eh-neon underline decoration-dotted">{site.email || 'contact@errorhacker.site'}</a></p>
          </Section>

          <div className="mt-10 text-center flex flex-wrap justify-center gap-2">
            <Link to="/terms" className="eh-btn-ghost text-xs">TERMS OF USE</Link>
            <Link to="/about" className="eh-btn-ghost text-xs">ABOUT US</Link>
            <Link to="/" className="eh-btn-primary text-xs">BACK TO HOME</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
